// BRK-S079 — the pure part of config-driven bond field handling: the value shapes
// and the "is this answered?" rule. Kept out of BondFieldRenderer.tsx so that file
// exports only a component (fast refresh), and because this is exactly the kind of
// pure logic the repo unit-tests directly.

import { EMPTY_ADDRESS, type AddressValue } from "@/lib/address";
import type {
  BondDocumentRef,
  BondFieldFormat,
  BondFieldOption,
  BondFieldType,
} from "@/lib/brokerBonds";
import { normalizeUsPhone } from "@/lib/phone";
import { US_STATES } from "@/lib/usStates";

/** A value a bond field can hold. `BondDocumentRef[]` is a `files` field's
 *  answer — the references PMS returned for the documents attached to it
 *  (BRK-S145). */
export type BondFieldValue =
  | string
  | number
  | boolean
  | AddressValue
  | BondDocumentRef[];

/** One step's captured values, keyed by the field's `path` (a fields step) or
 *  `key` (the final step). */
export type BondValueBag = Record<string, BondFieldValue | undefined>;

/** The shape both a config `fields` field and a `finalStep` field satisfy — the
 *  renderer doesn't care which addressing scheme the caller used. */
export interface BondRenderField {
  /** Stable identity for the value bag: a `path` (fields step) or `key` (final). */
  name: string;
  label: string;
  type: BondFieldType;
  required?: boolean;
  options?: BondFieldOption[];
  help?: string | null;
  /** Which VALUES are answers (BRK-S091, consuming SUR-S041's metadata) — see
   *  `BondFieldFormat`. Absent means the control type is the only constraint. */
  format?: BondFieldFormat;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  /**
   * BRK-S105 — the on-file line for a write-only PII field whose value PMS
   * already holds ("SSN on file (•••-••-1234) — leave blank to use it…").
   *
   * A RENDER concern rather than a wire one, which is why it lives on this
   * adapter type and not on `BondConfigField`: no configuration sends it, it is
   * derived from the insured's ACCOUNT read. It arrives already paired with
   * `required: false` (see `withPiiOnFile`), so the control, the required
   * checklist and the resume-position rule all waive the same field.
   */
  onFileNotice?: string;
  /** `files` only (BRK-S145) — how many documents the field takes, and a
   *  narrower accept list than the served rules. Both come straight off the
   *  configuration field; see `BondFinalField` for what they mean. */
  maxFiles?: number;
  accept?: string[];
}

/**
 * Whether a path names a REPEATED block — the contract's empty-bracket segment
 * (`personal[].lastName`), as opposed to an order-level path
 * (`business.legalName`).
 *
 * The distinction is about WHOSE answer a value is. An order-level path belongs
 * to the order; a repeated path belongs to ONE OWNER — the one PMS's roster
 * marks primary, resolved upstream (PMS-S244). That is the whole reason a
 * per-owner label exists on a step that has one of these and not on a step that
 * does not.
 *
 * Still an OPAQUE key otherwise: BRK stores by path and renders no repeats
 * (BRK-S094 owns that). This tests the grammar, it does not expand it.
 */
export function isRepeatedPath(path: string): boolean {
  return path.includes("[]");
}

/** Whether any of a step's fields addresses a repeated (per-owner) block. */
export function capturesOwnerValues(fields: BondRenderField[]): boolean {
  return fields.some((field) => isRepeatedPath(field.name));
}

// ── Which control a field gets (BRK-S142) ─────────────────────────────────────

/**
 * The control types this BUILD renders. As a value, so `fieldKind` cannot
 * disagree with `BondFieldType` — the same arrangement `stepKind` uses one
 * level up, and for the same reason.
 */
export const RENDERED_FIELD_TYPES = [
  "text",
  "textarea",
  "currency",
  "date",
  "number",
  "percent",
  "select",
  "checkbox",
  "address",
  "phone",
  "files",
] as const;

/** What a field's `type` can be classified as. `"unsupported"` is not a
 *  contract member — it is the answer for a `type` the CONTRACT publishes and
 *  this BUNDLE has never heard of. */
export type BondFieldKind = BondFieldType | "unsupported";

/**
 * Which control a field gets — `"unsupported"` for a `type` this bundle cannot
 * draw.
 *
 * THE ONE PLACE A FIELD'S CONTROL IS DECIDED, and the reason it exists rather
 * than a `switch (field.type)` at each site is exactly the reason `stepKind`
 * does: `BondFieldType` is a CLOSED union and discovery's vocabulary grows, so
 * a widened `type` arrives as a value the type system swears cannot exist. Every
 * `field.type === "…"` check simply returns false for it, and the renderer's
 * `default:` then quietly caught it — which is how an unknown type came to
 * render as a TEXT BOX.
 *
 * D-10 rule 1 is explicit that this is the wrong answer: "If a control cannot be
 * matched to the type, that is a **contract gap to fix, not a reason to fall
 * back to a text box**." A text box is not a degradation of a date picker or a
 * select — it accepts anything, so it invites a value the server will reject
 * several layers away, and it makes the gap invisible to everyone. So an
 * unrecognised type is NAMED here and rendered as a guarded card that blocks
 * Continue, the same treatment `stepKind` already gives an unrenderable STEP.
 *
 * A `state` FORMAT WINS OVER THE NAMED TYPE, and is answered first. The set of
 * US states is closed and lives in the client (BRK-S111), so the shared picker
 * is correct whatever control the configuration named — including a control
 * this build does not know. That makes it a `select` here rather than an
 * `unsupported`, which is what keeps this function and the renderer agreeing
 * about one field.
 */
export function fieldKind(field: BondRenderField): BondFieldKind {
  if (field.format === "state") return "select";
  const tag: string = field.type;
  const known = RENDERED_FIELD_TYPES.find((t) => t === tag);
  return known ?? "unsupported";
}

/**
 * The fields on a step this build cannot draw a control for — the step's reason
 * to block, and the list the blocking message names.
 *
 * Blocking is the point. An unsupported field is asked for by the configuration
 * and cannot be answered, so a step that let the broker past it would file an
 * order missing something Signal declared it needs, with nothing on screen
 * saying so. The step-level guard (`UnsupportedStep`) offers "continue without
 * it" because a whole step may be optional to the broker's progress; a FIELD is
 * part of a step they are otherwise completing, so the honest move is to stop
 * and tell them to reload.
 */
export function unsupportedFields(
  fields: readonly BondRenderField[],
): BondRenderField[] {
  return fields.filter((field) => fieldKind(field) === "unsupported");
}

export function isAddressValue(
  value: BondFieldValue | undefined,
): value is AddressValue {
  // An ARRAY is an object, so this used to answer true for a `files` value —
  // harmless while the union held no arrays, and a trap the moment it did:
  // `asAddress` would have read a document list as a half-typed address and
  // `isAnswered` would have called a field with three attachments unanswered.
  // The exclusion is here rather than at each call site because this predicate
  // is what every one of them asks.
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A value read as a `files` field's answer — always a list, so a caller never
 *  branches on absence. Anything that is not an array of references reads as
 *  empty: a stored bag is whatever the server holds (the BRK-S088 lesson), and
 *  a `files` field whose value is a leftover string has no attachments. */
export function asDocuments(
  value: BondFieldValue | undefined,
): BondDocumentRef[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is BondDocumentRef =>
      typeof entry === "object" &&
      entry !== null &&
      typeof (entry as BondDocumentRef).documentId === "string" &&
      typeof (entry as BondDocumentRef).filename === "string",
  );
}

/**
 * A value read as a COMPLETE address, filling anything missing from
 * `EMPTY_ADDRESS` and dropping any member that isn't a string.
 *
 * Needed because resume (BRK-S088) feeds SERVER-held values into these rules,
 * and a stored bag need not carry all four keys — where the create path only
 * ever produced them from `EMPTY_ADDRESS`. Reading a missing member as if it
 * were a string is how a resumed order white-screened the flow (NN #7), and a
 * controlled `AddressFieldset` needs all four to be strings regardless.
 */
export function asAddress(value: BondFieldValue | undefined): AddressValue {
  if (!isAddressValue(value)) return { ...EMPTY_ADDRESS };
  const from = value as Partial<Record<keyof AddressValue, unknown>>;
  const member = (key: keyof AddressValue): string =>
    typeof from[key] === "string" ? from[key] : "";
  return {
    street: member("street"),
    city: member("city"),
    state: member("state"),
    zip: member("zip"),
  };
}

/** A field's value as text (anything non-string reads as absent). */
export function asText(value: BondFieldValue | undefined): string {
  return typeof value === "string" ? value : "";
}

/** A field's value as a number, or "" so a numeric input can render empty. */
export function asNumber(value: BondFieldValue | undefined): number | "" {
  return typeof value === "number" ? value : "";
}

/** Whether a field has an answer. An address counts only when it is COMPLETE — a
 *  half-typed address is not an address. Zero counts: 0% is an answer. */
export function isAnswered(
  field: BondRenderField,
  value: BondFieldValue | undefined,
): boolean {
  // `fieldKind`, not `field.type`: the rules must ask the same question the
  // RENDERER answered, or a field drawn as a state picker would still be
  // judged as the address its configuration named (BRK-S142).
  const kind = fieldKind(field);
  // A `files` field is answered by at least one ATTACHED document. Not by a
  // pending upload: a reference exists only once PMS has stored the bytes, so
  // "answered" and "the server has it" are the same fact here by construction
  // — which is what stops a required field being satisfied by a file that
  // failed (BRK-S122's class).
  if (kind === "files") return asDocuments(value).length > 0;
  if (kind === "address") {
    if (!isAddressValue(value)) return false;
    const a = asAddress(value);
    return Boolean(
      a.street.trim() && a.city.trim() && a.state.trim() && a.zip.trim(),
    );
  }
  if (typeof value === "number") return true;
  // A required checkbox is an attestation: unticked is not an answer.
  if (typeof value === "boolean") return value;
  return asText(value).trim() !== "";
}

// ── Metadata-driven validation (BRK-S091, consuming SUR-S041) ─────────────────
// A control type says what a field LOOKS like; `format` says which values are
// answers. Both are enforced server-side as well — validating here means the
// broker learns before the round-trip, NOT that this is the only gate.

const STATE_CODES = new Set(US_STATES.map((s) => s.code));

/** Digits only, so punctuation the broker typed doesn't decide the verdict. */
function digitsOf(text: string): string {
  return text.replace(/\D/g, "");
}

/** A YYYY-MM-DD date input value as a UTC date, or null if unparseable. */
function parseDateValue(text: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
  const date = new Date(`${text}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** The date exactly `years` before `now`, in UTC. Turning 18 ON the boundary
 *  counts as 18, so the comparison is inclusive. */
function yearsBefore(now: Date, years: number): Date {
  const d = new Date(now.getTime());
  d.setUTCFullYear(d.getUTCFullYear() - years);
  return d;
}

function formatRuleError(
  format: BondFieldFormat,
  label: string,
  text: string,
  now: Date,
): string | null {
  switch (format) {
    case "email":
      // Deliberately loose: a name, an @, a dotted domain, no spaces. The server
      // is the authority; this catches the typo, not every RFC edge.
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)
        ? null
        : `${label} must be an email address.`;
    case "ssn":
    case "ein":
    case "nineDigits":
      return digitsOf(text).length === 9 ? null : `${label} must be 9 digits.`;
    case "state":
      return STATE_CODES.has(text.trim().toUpperCase())
        ? null
        : `${label} must be a 2-letter US state code.`;
    case "dateOfBirth": {
      const date = parseDateValue(text);
      if (date === null) return `${label} must be a date.`;
      if (date.getUTCFullYear() < 1900) return `${label} looks too far back.`;
      return date <= yearsBefore(now, 18)
        ? null
        : `${label} must be at least 18 years ago.`;
    }
    case "pastDate": {
      const date = parseDateValue(text);
      if (date === null) return `${label} must be a date.`;
      return date <= now ? null : `${label} cannot be in the future.`;
    }
  }
}

/**
 * Why a field's value isn't an answer, or null when it is. An EMPTY value is
 * always null: absence is `isAnswered`'s business, and reporting a format
 * complaint on an untouched field reads as an error the broker caused.
 *
 * `now` is injected so the date rules are pure and testable.
 */
export function fieldError(
  field: BondRenderField,
  value: BondFieldValue | undefined,
  now: Date = new Date(),
): string | null {
  const kind = fieldKind(field);

  if (typeof value === "number") {
    // A percent is 0–100 by its own nature; explicit bounds still win.
    const min = field.min ?? (kind === "percent" ? 0 : undefined);
    const max = field.max ?? (kind === "percent" ? 100 : undefined);
    if (min !== undefined && value < min) {
      return `${field.label} cannot be less than ${min}.`;
    }
    if (max !== undefined && value > max) {
      return `${field.label} cannot be more than ${max}.`;
    }
    return null;
  }

  const text = asText(value).trim();
  if (text === "") return null;

  // A phone's rule rides on the CONTROL, not on a `format` — the contract gives
  // `phone` its own control and its own canonical value, and no format names
  // it. The verdict is the API's own (`normalizeUsPhone` is a verbatim port of
  // the PMS helper, BRK-S073), so the broker learns before the round-trip
  // rather than after one (BRK-S111).
  if (kind === "phone") {
    const result = normalizeUsPhone(text);
    if (!result.ok) return result.reason;
  }

  if (field.format !== undefined) {
    const error = formatRuleError(field.format, field.label, text, now);
    if (error !== null) return error;
  }
  if (field.minLength !== undefined && text.length < field.minLength) {
    return `${field.label} must be at least ${field.minLength} characters.`;
  }
  if (field.maxLength !== undefined && text.length > field.maxLength) {
    return `${field.label} must be at most ${field.maxLength} characters.`;
  }
  return null;
}

/** Every field whose value breaks a rule, paired with the reason — the same
 *  per-field-checklist shape `unansweredFields` feeds. */
export function invalidFields(
  fields: BondRenderField[],
  values: BondValueBag,
  now: Date = new Date(),
): Array<{ field: BondRenderField; reason: string }> {
  return fields.flatMap((field) => {
    const reason = fieldError(field, values[field.name], now);
    return reason === null ? [] : [{ field, reason }];
  });
}

/**
 * A captured bag as the CONTRACT wants it, rather than as it renders (BRK-S111).
 *
 * D-10's display/wire split is load-bearing and "runs BOTH ways", and the
 * pinned contract spells a `phone` value out: "a bare E.164 string like
 * `+15125550100` (humanised only at render — never a `{number}` record)". The
 * control humanises as the broker types, which is correct and is what
 * `maskPhoneInput` is for — so the READ direction was never wrong. The write
 * was: the flow saved the display string, and `(201) 555-0123` is what reached
 * the order. Driving the built bundle is what surfaced it.
 *
 * ONLY `phone` is touched. Every other control already stores its canonical
 * value — numerics are numbers, dates are ISO, an address is the composite —
 * and a transform here that "helped" any of them would be the same
 * quietly-repairing-on-the-way-past that BRK-S110 removed from the display side.
 *
 * A value that will not normalise is passed through UNCHANGED, never dropped
 * and never guessed at: `fieldError` is what stops it reaching the wire, and if
 * one somehow does, the server should see what the broker actually typed rather
 * than a number nobody entered (NN #7).
 */
export function bondWireValues(
  fields: readonly BondRenderField[],
  values: BondValueBag,
): BondValueBag {
  const phonePaths = new Set(
    fields.filter((f) => fieldKind(f) === "phone").map((f) => f.name),
  );
  if (phonePaths.size === 0) return values;

  const out: BondValueBag = { ...values };
  for (const path of phonePaths) {
    const value = out[path];
    if (typeof value !== "string" || value.trim() === "") continue;
    const result = normalizeUsPhone(value);
    if (result.ok) out[path] = result.e164;
  }
  return out;
}

/** The required fields a step is still missing, for a per-field checklist rather
 *  than one generic "complete the form" (the BRK-S052 missing-fields pattern). */
export function unansweredFields(
  fields: BondRenderField[],
  values: BondValueBag,
): BondRenderField[] {
  return fields.filter((f) => f.required && !isAnswered(f, values[f.name]));
}
